import { LitElement, html, PropertyValues } from 'lit';
import toArray from '@loadsmart/miranda-toolset/dist/to-array';
import { classMap } from 'lit/directives/class-map.js';
import SelectionController, {
	AcceptedType,
	SelectionState,
	SelectionType,
} from '../../controllers/selection';
import { RADIO_EVENT } from './radio-group.constants';
import type { Radio } from './radio';
import styles from './radio-group.styles';

export type RadioGroupOrientation = 'vertical' | 'horizontal';

export type RadioGroupChangeEventDetail = {
	type: SelectionType;
	value: SelectionState;
};
export interface RadioGroupProps {
	value?: AcceptedType;
	disabled?: boolean;
	orientation?: RadioGroupOrientation;
	'm-change'?: (event: CustomEvent<RadioGroupChangeEventDetail>) => void;
}

export class RadioGroup extends LitElement {
	static override styles = [styles()];

	#selectionController: SelectionController | null = null;

	#defaultSlot: HTMLSlotElement | null = null;

	static override get properties() {
		return {
			value: { type: String, reflect: true },
			disabled: { type: Boolean, reflect: true },
			selection: { state: true },
			orientation: { type: String },
		};
	}

	/**
	 * Value of the selected radio-button.
	 */
	declare value: RadioGroupProps['value'];

	/**
	 * Type of the selected radio-button.
	 *  It is necessary due to elegibility as a SelectionHost for the SelectionController
	 */
	declare type: SelectionType;

	/**
	 * Should the button be disabled.
	 */
	declare disabled: boolean;

	/**
	 * Inner toggle selection state.
	 */
	declare selection: SelectionState;

	/**
	 * Buttons display orientation (vertical or horizontal)
	 */
	declare orientation: RadioGroupOrientation;

	constructor() {
		super();
		this.disabled = false;
		this.selection = new Set();
		this.value = '';
		this.orientation = 'vertical';
		this.type = 'single-strict';
	}

	override connectedCallback(): void {
		super.connectedCallback();

		this.setAttribute('role', 'radio-group');

		this.#selectionController = new SelectionController(
			this,
			this.type,
			RADIO_EVENT,
		);
	}

	protected override update(changedProperties: PropertyValues<this>): void {
		super.update(changedProperties);

		if (changedProperties.has('value') && this.#selectionController) {
			this.#selectionController.init(
				toArray(this.value || this.#getDefaultChecked()),
			);
		}

		if (changedProperties.has('selection')) {
			[this.value] = this.selection;
		}
	}

	protected override updated(changedProperties: PropertyValues<this>): void {
		super.updated(changedProperties);

		if (changedProperties.has('selection')) {
			this.#getButtons().forEach((toggle) => {
				this.#setButtonChecked(toggle);
			});
		}

		if (changedProperties.has('disabled')) {
			this.#getButtons().forEach((toggle) => {
				this.#setButtonDisabled(toggle);
			});
		}
	}

	override render() {
		return html`
			<div
				class=${classMap({
					'radio-group': true,

					'-vertical': this.orientation === 'vertical',
					'-horizontal': this.orientation === 'horizontal',
				})}
			>
				<slot></slot>
			</div>
		`;
	}

	#getDefaultChecked = (): string | undefined => {
		/**
		 * If no radio component is marked as checked, set the first one as checked and return its value
		 */
		const buttons = this.#getButtons();
		return buttons[0]?.value;
	};

	#getButtons = (): Radio[] => {
		this.#defaultSlot ??=
			this.shadowRoot && this.shadowRoot.querySelector('slot');
		const items = this.#defaultSlot?.assignedElements({ flatten: true }) ?? [];

		return [...items].filter(
			(item) => item.tagName.toLowerCase() === 'm-radio',
		) as Radio[];
	};

	#setButtonChecked = (button: Radio) => {
		// eslint-disable-next-line no-param-reassign
		button.checked = this.selection.has(button.value);
	};

	#setButtonDisabled = (button: Radio) => {
		// eslint-disable-next-line no-param-reassign
		button.disabled = this.disabled;
	};
}

window.customElements.define('m-radio-group', RadioGroup);

declare global {
	interface HTMLElementTagNameMap {
		'm-radio-group': RadioGroup;
	}
}
